import DataGrid, {
    Editing, Paging, Column, Pager, Scrolling, FilterRow, HeaderFilter, DataGridTypes, RequiredRule, Lookup,
    MasterDetail, Button, Texts, Toolbar, Item, Sorting, Selection, GroupPanel,
    KeyboardNavigation,
    ColumnChooser,
    SearchPanel,
    Export,
    CustomRule
} from 'devextreme-react/data-grid';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DataGridWrapperProps } from '@/core/interfaces/usestate-interfaces';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import * as authHelper from '@modules/auth/core/AuthHelpers'
import { Workbook } from 'exceljs';
import { exportDataGrid } from 'devextreme/excel_exporter';
import saveAs from 'file-saver';
import { useNavigate } from 'react-router-dom';
import { checkPermission } from '@modules/auth/core/AuthHelpers';


const DataGridWrapper: React.FC<DataGridWrapperProps> = ({
    gridRef,
    datasource,
    columns,
    pagerVisible,
    filterVisible,
    searchPanelVisible = true,
    headerFilterVisible,
    masterGridRef,
    customToolbarItems,
    enableCustomToolbarContent,
    remoteOperations,
    editingConfig,
    props,
    masterDetail = undefined,
    buttonConfig = [],
    sortingMode = 'single',
    grouping,
    selectionConfig,
    height,
    permissonCode,
    keyboardNavigation,
    columnChooser = true,
    showColumnLines,
    excellExport,
    navigateNewRecordPath,
    isAddNewBtn = true,
    columnAutoWidth,
    wordWrapEnabled,
    onFocusedCellChanging,
    onSaving,
    onEditingStart,
    onRowUpdating,
    onInitNewRow,
    onEditorPreparing,
    onToolbarPreparing,
    onInitialized,
    onSelectionChanged,
    onContentReady,
    onCellPrepared,
    onCellEditingStart }) => {

    const allowedPageSizes: (DataGridTypes.PagerPageSize | number)[] = [20, 50, 100];
    const navigate = useNavigate();

    const onDataErrorOccurred = (e: any) => {
        try {
            toast.error(e.error.response.data.message);
        } catch (error) {
            toast.error(t("failed"));
        }
    }

    const onSaved = (e: any) => {
        if (remoteOperations !== false)
            toast.success(t("saved_changes"));
    }


    const checkPermissions = (action: string): boolean => {
        let permissonResult = true;
        if (permissonCode) {
            permissonResult = authHelper.checkPermission(permissonCode, action);
        }
        return permissonResult;
    }
    const onExporting = (e: DataGridTypes.ExportingEvent) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
            });
        });
    };

    const newRecordButtonOptions = useMemo(() => ({
        text: t('new_record'),
        type: "default", // Butonu mavi yapmak için
        stylingMode: "contained", // Dolgun buton görünümü
        icon: 'add',
        elementAttr: {
            class: "custom-toolbar-button", // Özel CSS için sınıf ekliyoruz
        },
        onClick: () => {
            if (navigateNewRecordPath) {
                navigate(navigateNewRecordPath)
            }
        },
    }), []);

    return (
        <React.Fragment>
            <div style={{ height: "70vh", display: "flex", flexDirection: "column" }}>
                <DataGrid
                    columnAutoWidth={columnAutoWidth != undefined ? columnAutoWidth : false}
                    wordWrapEnabled={wordWrapEnabled != undefined ? wordWrapEnabled : false}
                    ref={gridRef}
                    showBorders={true}
                    showColumnLines={showColumnLines == undefined ? false : showColumnLines}
                    dataSource={datasource}
                    repaintChangesOnly={true}
                    rowAlternationEnabled={true}
                    width="100%"
                    onFocusedCellChanging={onFocusedCellChanging}
                    onSaving={onSaving}
                    onExporting={onExporting}
                    onEditingStart={onEditingStart}
                    onRowUpdating={onRowUpdating}
                    onInitNewRow={onInitNewRow}
                    onEditorPreparing={onEditorPreparing}
                    // onToolbarPreparing={onToolbarPreparing}
                    onInitialized={onInitialized}
                    onSelectionChanged={onSelectionChanged}
                    onDataErrorOccurred={onDataErrorOccurred}
                    onContentReady={onContentReady}
                    onCellPrepared={onCellPrepared}
                    onCellEditingStart={onCellEditingStart}
                    onSaved={onSaved}
                    errorRowEnabled={false}
                    allowColumnResizing={true}
                    height={height != undefined ? height : "100%"}
                    remoteOperations={remoteOperations != undefined ? remoteOperations : true}
                    {...props}
                    onToolbarPreparing={(e) => {
                        if (onToolbarPreparing) {
                            onToolbarPreparing(e);
                        }
                        const toolbarItems = e.toolbarOptions.items;
                        toolbarItems?.forEach(function (item: any) {
                            if (item.name === "searchPanel") {
                                item.location = 'before';
                            }
                        })
                    }}
                >
                    <SearchPanel visible={searchPanelVisible} placeholder="Ara..." />
                    <ColumnChooser enabled={columnChooser} mode='select'></ColumnChooser>

                    <Sorting mode={sortingMode} />
                    <Scrolling mode={"standard"} showScrollbar={'onHover'} />
                    <FilterRow visible={filterVisible} />

                    {
                        keyboardNavigation &&
                        <KeyboardNavigation
                            editOnKeyPress={true}
                            enterKeyAction="startEdit"
                            enterKeyDirection="column"
                        />
                    }

                    <HeaderFilter visible={headerFilterVisible}
                        search={{ enabled: true }}
                        allowSelectAll={true} />

                    {editingConfig && <Editing
                        refreshMode={editingConfig.refreshMode}
                        mode={editingConfig.mode}
                        allowAdding={editingConfig.allowAdding && checkPermissions('Create')}
                        allowUpdating={editingConfig.allowUpdating && checkPermissions('Update')}
                        allowDeleting={editingConfig.allowDeleting && checkPermissions('Delete')}
                        confirmDelete={editingConfig.confirmDelete}
                        useIcons={editingConfig.useIcons}
                        startEditAction="dblClick"
                    >
                        <Texts

                            confirmDeleteMessage={editingConfig.deleteMessageText}
                            confirmDeleteTitle={t('warning')}
                        />
                    </Editing>}

                    {editingConfig == undefined && <Editing
                        refreshMode={"reshape"}
                        mode={'batch'}
                        allowAdding={checkPermissions('Create')}
                        allowDeleting={checkPermissions('Delete')}
                        allowUpdating={checkPermissions('Update')}
                    >
                    </Editing>}

                    <Paging defaultPageSize={20} />
                    <Pager
                        showNavigationButtons={true}
                        showInfo={true}
                        allowedPageSizes={allowedPageSizes}
                        showPageSizeSelector={true}
                        visible={pagerVisible}
                    />
                    {grouping &&
                        <GroupPanel visible={true} />}
                    {columns.map((col: any, index: any) => {
                        if (col)
                            return (
                                <Column key={index} {...col} allowEditing={col.allowEditing} >

                                    {
                                        col.customRequired
                                        &&
                                        <CustomRule
                                            type="custom"
                                            validationCallback={col.callFunc}
                                            message={col.customErrorMessage}
                                        />
                                    }

                                    {col.required && <RequiredRule message={col.requiredMessage} />}
                                    {col.lookup && (
                                        <Lookup
                                            dataSource={col.lookup.dataSource}
                                            valueExpr={col.lookup.valueExpr}
                                            displayExpr={col.lookup.displayExpr}
                                        />
                                    )}
                                    {col.type == 'buttons' &&
                                        col.buttons.map((btn: any, index: number) => (
                                            <Button
                                                key={index}
                                                name={btn.name}
                                                hint={btn.hint}
                                                onClick={btn.onClick}

                                            />
                                        ))
                                    }
                                </Column>
                            )
                    })}

                    {buttonConfig.length > 0 && <Column type="buttons">
                        {buttonConfig.map((btn: any, index: number) => (
                            <Button
                                key={index}
                                name={btn.name}
                                hint={btn.hint}
                                onClick={btn.onClick}
                            />
                        ))}
                    </Column>}
                    {masterDetail && masterGridRef &&
                        <MasterDetail enabled={true} component={masterDetail} ref={masterGridRef} />
                    }
                    {masterDetail &&
                        <MasterDetail enabled={true} component={masterDetail} />
                    }
                    {enableCustomToolbarContent &&
                        <Toolbar>
                            <Item location="before" name="searchPanel" />
                            <Item name="columnChooserButton" />
                            <Item name="groupPanel" />
                            {customToolbarItems &&
                                customToolbarItems.map((item: any, index: number) => (
                                    <Item
                                        widget={item.widget}
                                        location={item.location}
                                        options={item.options}
                                    />
                                ))}
                            {isAddNewBtn &&
                                permissonCode && checkPermission(permissonCode, "Create") &&
                                <Item
                                    widget="dxButton"
                                    location="after"
                                    options={newRecordButtonOptions}
                                />
                            }
                        </Toolbar>
                        // customToolbarContent
                    }
                    {selectionConfig &&
                        <Selection {...selectionConfig} />
                    }
                    {excellExport &&
                        <Export enabled={true} allowExportSelectedData={true} />
                    }

                </DataGrid>
            </div>
        </React.Fragment>
    );
};

export default DataGridWrapper;